import {
  Box, Button, Checkbox, Flex, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import { BatchCardInfo } from "../BatchCardInfo";
import { EditBatch } from "../EditBatch";
import { FiCalendar, FiPackage } from "react-icons/fi";
import swal from "sweetalert";
import { format } from "date-fns";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

function FormField({ label, children }) {
  return (
    <Box mb="12px">
      <Text fontSize="11px" fontWeight="700" color="gray.500" mb="4px" textTransform="uppercase" letterSpacing="0.05em">{label}</Text>
      {children}
    </Box>
  );
}

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

export function BatchCard({ productId, productName, allBatches, allPrices, productBatch }) {
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [openBatches, setOpenBatches] = useState(allBatches);
  const [showAddress, setShowAddress] = useState(false);
  const [CEP, setCEP] = useState("");
  const [batchAmount, setBatchAmount] = useState("");
  const [batchDiscount, setBatchDiscount] = useState("");
  const [batchStartDate, setBatchStartDate] = useState("");
  const [batchExpirationDate, setBatchExpirationDate] = useState("");
  const [batchUnity, setBatchUnity] = useState("");
  const [address, setAddress] = useState({});
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [batch, setBatch] = useState(productBatch);

  async function fetchCEP(e) {
    e.preventDefault();
    const response = await fetch(`https://viacep.com.br/ws/${CEP}/json/`);
    const data = await response.json();
    setAddress(data);
  }

  async function handleCreate(e) {
    e.preventDefault();
    const data = {
      batch: {
        stock: batchAmount,
        discount_per_centage: batchDiscount,
        start_date: batchStartDate,
        expiration_date: batchExpirationDate,
        product_id: productId,
        collection_address_id: null,
        unity_of_measurement: batchUnity,
      },
      collection_address: {
        address_street: address.logradouro,
        address_number: addressNumber,
        address_complement: addressComplement,
        address_neighborhood: address.bairro,
        city_id: address.localidade,
        state_id: address.uf,
        address_postal_code: address.cep,
      },
    };
    const response = await api.post("/api/v1/batches/create_batch_and_collection_address", data, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setOpenBatches(prev => [...prev, response.data]);
      swal({ title: "Criado!", text: "Lote criado com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
    onClose();
  }

  async function handleGetPreviousBatchInfos(e) {
    if (!e.target.checked) {
      setBatchAmount("");
      setBatchDiscount("");
      setBatchUnity("");
      setCEP("");
      setAddress({});
      setShowAddress(false);
      return;
    }
    try {
      const response = await api.get(`/api/v1/admin/panels/get_previous_batch_infos?product_id=${productId}`, {
        headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
      });
      if (response.data.message) {
        alert(response.data.message);
        e.target.checked = false;
        return;
      }
      const postal = response.data.address.address_postal_code.replace(/-/g, "");
      setCEP(postal);
      const cepRes = await fetch(`https://viacep.com.br/ws/${postal}/json/`);
      setAddress(await cepRes.json());
      setShowAddress(true);
      setBatchAmount(response.data.batch.stock);
      setBatchDiscount(response.data.batch.discount_per_centage);
      setBatchUnity(response.data.batch.unity_of_measurement);
    } catch {
      swal({ title: "Erro", text: "Não foi possível carregar informações do lote anterior.", icon: "error" });
    }
  }

  const updateBatch = (updatedBatch) => {
    setOpenBatches(prev => prev.map(b => b.id === updatedBatch.id ? updatedBatch : b));
  };

  const today = new Date();
  const foundBatch = openBatches?.find(b => b.product_id === productId && new Date(b.expiration_date) >= today);
  const notStarted = foundBatch && new Date(foundBatch.start_date) > today;

  return (
    <>
      {foundBatch ? (
        <Button
          size="xs" borderRadius="7px" px="10px" h="26px" fontWeight="700" fontSize="11px"
          bg={notStarted ? "rgba(41,128,185,.12)" : "rgba(39,174,96,.12)"}
          color={notStarted ? "#2980b9" : "#27ae60"}
          border={`1px solid ${notStarted ? "rgba(41,128,185,.3)" : "rgba(39,174,96,.3)"}`}
          _hover={{ opacity: 0.8 }}
          leftIcon={notStarted ? <FiCalendar size={11} /> : <FiPackage size={11} />}
          onClick={onOpen}
        >
          {notStarted ? `Abre ${format(new Date(foundBatch.start_date), "dd/MM")}` : "Ver lote"}
        </Button>
      ) : (
        <Button
          size="xs" borderRadius="7px" px="10px" h="26px" fontWeight="700" fontSize="11px"
          bg="rgba(231,76,60,.08)" color="#e74c3c" border="1px solid rgba(231,76,60,.25)"
          _hover={{ opacity: 0.8 }}
          onClick={onOpen}
        >
          Gerar lote
        </Button>
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
        <ModalOverlay bg="rgba(0,0,0,.45)" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="16px" overflow="hidden" mx="16px">
          <ModalHeader bg={GREEN} py="14px" px="20px" pr="44px">
            <Text fontSize="15px" fontWeight="800" color={ORANGE}>
              {foundBatch ? "Lote Ativo" : "Gerar Novo Lote"}
            </Text>
            <Text fontSize="11px" color="rgba(255,255,255,.45)" fontWeight="500" mt="1px" noOfLines={1}>
              {productName}
            </Text>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,.6)" _hover={{ color: "white", bg: "transparent" }} top="12px" right="14px" />

          <ModalBody px="20px" py="20px" bg="#fafbfc">
            {foundBatch ? (
              <BatchCardInfo batch={foundBatch} productId={productId} prices={allPrices} />
            ) : (
              <Box>
                <Checkbox
                  colorScheme="green" mb="14px"
                  onChange={handleGetPreviousBatchInfos}
                  fontSize="13px" fontWeight="600"
                >
                  Usar informações do lote anterior
                </Checkbox>

                <Flex gap="10px">
                  <Box flex="1">
                    <FormField label="Estoque">
                      <Input
                        placeholder="Qtd. unidades"
                        type="number"
                        value={batchAmount}
                        onChange={e => setBatchAmount(e.target.value)}
                        {...inputProps}
                      />
                    </FormField>
                  </Box>
                  <Box flex="1">
                    <FormField label="Desconto (%)">
                      <Input
                        placeholder="0"
                        type="number"
                        value={batchDiscount}
                        onChange={e => setBatchDiscount(e.target.value)}
                        {...inputProps}
                      />
                    </FormField>
                  </Box>
                </Flex>

                <Flex gap="10px">
                  <Box flex="1">
                    <FormField label="Data de início">
                      <Input type="date" onChange={e => setBatchStartDate(e.target.value)} {...inputProps} />
                    </FormField>
                  </Box>
                  <Box flex="1">
                    <FormField label="Data de expiração">
                      <Input type="date" onChange={e => setBatchExpirationDate(e.target.value)} {...inputProps} />
                    </FormField>
                  </Box>
                </Flex>

                <FormField label="Unidade de medida">
                  <Select value={batchUnity} onChange={e => setBatchUnity(e.target.value)} {...inputProps}>
                    <option value="">Selecionar unidade</option>
                    <option value="Caixa">Caixa</option>
                    <option value="Fardo">Fardo</option>
                    <option value="Pacote">Pacote</option>
                    <option value="Unidade">Unidade</option>
                  </Select>
                </FormField>

                <Button
                  size="sm" w="100%" borderRadius="8px" mt="4px" mb="14px"
                  variant="outline" colorScheme={showAddress ? "red" : "green"}
                  fontWeight="600" fontSize="12px"
                  onClick={() => setShowAddress(!showAddress)}
                >
                  {showAddress ? "Remover endereço de retirada" : "+ Adicionar endereço de retirada"}
                </Button>

                {showAddress && (
                  <Box
                    bg="white" borderRadius="10px" p="14px"
                    border="1px solid rgba(0,74,51,.12)" mb="4px"
                  >
                    <Text fontSize="12px" fontWeight="700" color={GREEN} mb="10px">Endereço de retirada</Text>
                    <Flex gap="10px" mb="10px">
                      <Input
                        flex="1" placeholder="CEP (somente números)"
                        type="number" value={CEP}
                        onChange={e => setCEP(e.target.value)}
                        {...inputProps}
                      />
                      <Button
                        borderRadius="8px" px="16px" bg={GREEN} color={ORANGE}
                        fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
                        onClick={fetchCEP}
                      >
                        Buscar
                      </Button>
                    </Flex>
                    {address.logradouro && (
                      <>
                        <Flex gap="10px" mb="10px">
                          <Input flex="2" value={address.logradouro || ""} placeholder="Rua" disabled {...inputProps} />
                          <Input flex="1" placeholder="Número" type="number" onChange={e => setAddressNumber(e.target.value)} {...inputProps} />
                        </Flex>
                        <Flex gap="10px">
                          <Input placeholder="Complemento" onChange={e => setAddressComplement(e.target.value)} {...inputProps} />
                          <Input value={address.bairro || ""} placeholder="Bairro" disabled {...inputProps} />
                        </Flex>
                        <Flex gap="10px" mt="10px">
                          <Input value={address.localidade || ""} placeholder="Cidade" disabled {...inputProps} />
                          <Input value={address.uf || ""} placeholder="Estado" disabled {...inputProps} />
                        </Flex>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter bg="white" borderTop="1px solid rgba(0,74,51,.08)" px="20px" py="12px" gap="8px" justifyContent="flex-end">
            <Button size="sm" variant="ghost" colorScheme="gray" borderRadius="8px" onClick={onClose}>Fechar</Button>
            {!foundBatch && (
              <Button size="sm" bg={GREEN} color={ORANGE} borderRadius="8px" px="16px" fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }} onClick={handleCreate}>
                Cadastrar lote
              </Button>
            )}
            {foundBatch && (
              <EditBatch productId={productId} productBatch={batch || foundBatch} onUpdateBatch={updateBatch} />
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
